import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../lib/core";
import {
  Box,
  Drawer,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Divider,
  IconButton,
  Fab,
  TextField,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";

const API_URL = "https://aiassistant-mhuxhbx3ca-uc.a.run.app";

export default function AiAssistantChat() {
  const { authUser } = useContext(AuthContext);
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Generate a sessionId once per visit
  useEffect(() => {
    const id = "session-" + Math.random().toString(36).substring(2, 10);
    setSessionId(id);
  }, []);
  // Auto-scroll to bottom when messages, loading, or drawer open change
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) {
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } catch (e) {
        el.scrollTop = el.scrollHeight;
      }
    } else if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      } catch (e) {
        messagesEndRef.current.scrollIntoView();
      }
    }
  }, [messages, loading, open]);

  // Hide the assistant for Nurse users (check after hooks)
  if (authUser && authUser.userType === "Nurse") return null;

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId,
          message: input,
        }),
      });

      const data = await res.json();
      const aiMessage = { role: "assistant", text: data.reply };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Sorry, something went wrong." },
      ]);
    }

    setLoading(false);
  };

  return (
    <>
      {/* Floating Button */}
      <Fab
        color="primary"
        aria-label="open chat"
        sx={{ position: 'fixed', bottom: { xs: 80, sm: 24 }, right: { xs: 16, sm: 24 } }}
        onClick={() => setOpen(true)}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Drawer (swipeable) */}
      <SwipeableDrawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        disableBackdropTransition={!isMobile}
      >
        <Box
          sx={{
            width: { xs: '100vw', sm: 360 },
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppBar position="static" color="primary" elevation={1}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">AI Assistant</Typography>
              <IconButton edge="end" color="inherit" onClick={() => setOpen(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Divider />

          {/* Messages */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
            }}
            ref={messagesContainerRef}
          >
            {messages.map((m, i) => (
              <Paper
                key={i}
                style={{
                  padding: 12,
                  maxWidth: "80%",
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  backgroundColor:
                    m.role === "user" ? "#bbdefb" : "rgb(238, 238, 238)",
                }}
              >
                <Typography>{m.text}</Typography>
              </Paper>
            ))}

            <div ref={messagesEndRef} />

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>

          {/* Input (sticky) */}
          <Box sx={{ position: 'sticky', bottom: 0, p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              />
              <IconButton color="primary" onClick={sendMessage} sx={{ alignSelf: 'center' }}>
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
